import React, { useEffect, useRef, useState } from "react";
import CytoscapeComponent from "react-cytoscapejs";
import cytoscape from "cytoscape";
import dagre from "cytoscape-dagre";
import {
  fetchStationNodes,
  fetchChildNodes,
  fetchStatusUpdate,
  saveNodePosition,
  saveViewPreferences,
  fetchViewPreferences,
  fetchSeverityColors,
  fetchSvgTemplates,
  clearNodePositions,
} from "./LinkViewAPI";
import { useMaterialUIController } from "context";

cytoscape.use(dagre);

// ---------------- Severity & Colors ----------------

// Severity order: higher numbers are more severe (descending priority)
// Supports severity levels 0-21
const SEVERITY_ORDER = [21, 20, 19, 18, 17, 16, 15, 14, 13, 12, 11, 10, 9, 8, 7, 6, 5, 4, 3, 2, 1]; // Higher number = more severe

function severityColor(sev, severityColors) {
  if (!severityColors || !Array.isArray(severityColors)) {
    return "#3f51b5"; // fallback
  }
  
  // sev can be either a severity ID (number) or severity name (string)
  let found;
  if (typeof sev === 'number') {
    // Search by ID
    found = severityColors.find(item => item.id === sev);
  } else {
    // Search by severity name
    const key = sev ? sev.toUpperCase() : "NONE";
    found = severityColors.find(item => item.severity === key);
  }
  
  if (found) return found.color;
  
  const none = severityColors.find(item => item.severity === "NONE" || item.id === 0);
  return none ? none.color : "#3f51b5";
}

function highestSeverity(status, alarms) {
  if (status === "down") {
    return 21; // Most severe level when down
  }
  if (alarms && alarms.length > 0) {
    // Find the highest severity number (most severe)
    const maxSeverity = Math.max(...alarms.map(a => a.severity || 0));
    return maxSeverity;
  }
  return 0; // NONE - no issues
}

// ---------------- Color Conversion ----------------

function rgbToHex(rgb) {
  // Convert rgb(r, g, b) or rgba(r, g, b, a) to #rrggbb
  if (!rgb || typeof rgb !== "string") return "#fafafa";
  if (rgb.startsWith("#")) return rgb;
  const match = rgb.match(/\d+/g);
  if (!match || match.length < 3) return "#fafafa";
  const [r, g, b] = match.map(Number);
  return "#" + [r, g, b].map((x) => x.toString(16).padStart(2, "0")).join("");
}

// ---------------- Transparent SVG Icons ----------------

// SVG templates cache - loaded from API
let svgTemplatesCache = {};

function getSvgTemplate(type, svgTemplates = null) {
  // Use provided templates or cache
  const templates = svgTemplates || svgTemplatesCache;
  
  // Check if we have a template from API for this type (exact match first)
  if (templates[type]) {
    // Handle both new object format { svgIcon, managementURL } and legacy string format
    const result = typeof templates[type] === 'object' ? templates[type].svgIcon : templates[type];
    // If svgIcon is null/undefined/empty, fall through to default templates
    if (result) {
      return result;
    }
  }
  
  // Try case-insensitive lookup if exact match fails
  if (templates && typeof templates === 'object') {
    const typeUpper = type.toUpperCase();
    const matchingKey = Object.keys(templates).find(key => key.toUpperCase() === typeUpper);
    if (matchingKey && templates[matchingKey]) {
      const result = typeof templates[matchingKey] === 'object' ? templates[matchingKey].svgIcon : templates[matchingKey];
      if (result) {
        return result;
      }
    }
  }
  
  // Fallback to default templates if API doesn't have this type
  switch (type) {
    case "router":
      return `
    <svg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
  <rect x="15" y="35" width="70" height="30" rx="6"
        stroke="{{COLOR}}" fill="none" stroke-width="4"/>
  <circle cx="30" cy="50" r="5" fill="{{COLOR}}" />
  <circle cx="50" cy="50" r="5" fill="{{COLOR}}" />
  <circle cx="70" cy="50" r="5" fill="{{COLOR}}" />
</svg>`;
    case "switch":
      return `
    <svg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
  <rect x="10" y="30" width="80" height="40" rx="6"
        stroke="{{COLOR}}" fill="none" stroke-width="4"/>
  <rect x="18" y="45" width="12" height="6" fill="{{COLOR}}"/>
  <rect x="36" y="45" width="12" height="6" fill="{{COLOR}}"/>
  <rect x="54" y="45" width="12" height="6" fill="{{COLOR}}"/>
  <rect x="72" y="45" width="12" height="6" fill="{{COLOR}}"/>
</svg>`;
    case "tower":
      return `
    <svg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
  <polygon points="50,15 58,35 42,35" fill="{{COLOR}}"/>
  <rect x="46" y="35" width="8" height="50" fill="{{COLOR}}"/>
  <line x1="50" y1="40" x2="25" y2="85" stroke="{{COLOR}}" stroke-width="6"/>
  <line x1="50" y1="40" x2="75" y2="85" stroke="{{COLOR}}" stroke-width="6"/>
</svg>`;
    case "gateway":
      return `
    <svg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
  <circle cx="50" cy="50" r="40" stroke="{{COLOR}}" stroke-width="8" fill="none"/>
  <polygon points="50,25 65,50 50,75" fill="{{COLOR}}" />
  <polygon points="50,25 35,50 50,75" fill="{{COLOR}}" />
</svg>`;
    default:
      return `
    <svg width="100" height="100" viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
  <circle cx="50" cy="50" r="42" stroke="{{COLOR}}" stroke-width="8" fill="none"/>
  <circle cx="50" cy="50" r="28" fill="{{COLOR}}"/>
</svg>`;
  }
}

function buildSvgDataUri(type, color, bg, svgTemplates = null, childrenSeverity = 0, severityColors = null) {
  let svg = getSvgTemplate(type, svgTemplates).replace(/{{COLOR}}/g, color);
  // Use the detected background or fallback to #fafafa (the container default)
  const bgColor = rgbToHex(bg) || "#fafafa";
  svg = svg.replace(/{{BG}}/g, bgColor);
  
  // Add badge circle if childrenSeverity > 0
  if (childrenSeverity > 0 && severityColors) {
    const badgeColor = severityColor(childrenSeverity, severityColors);
    // Insert badge circle before closing </svg> tag
    // Position it at top-right corner (85, 15) with radius 10 (20% of 100x100 viewBox / 2)
    const badge = `<circle cx="85" cy="15" r="10" fill="${badgeColor}" stroke="#fff" stroke-width="2"/>`;
    svg = svg.replace('</svg>', badge + '</svg>');
  }
  
  // Use base64 encoding for the SVG data URI to avoid rendering issues
  // `btoa(unescape(encodeURIComponent(...)))` safely encodes UTF-8 to base64 in browsers
  try {
    if (typeof window !== "undefined" && window.btoa) {
      const base64 = window.btoa(unescape(encodeURIComponent(svg)));
      return `data:image/svg+xml;base64,${base64}`;
    }
  } catch (e) {
    // fall through to Node Buffer fallback
  }
  // Node.js environment fallback
  if (typeof Buffer !== "undefined") {
    return `data:image/svg+xml;base64,${Buffer.from(svg, "utf8").toString(
      "base64"
    )}`;
  }
  // Last-resort: percent-encode
  const encoded = encodeURIComponent(svg)
    .replace(/'/g, "%27")
    .replace(/\"/g, "%22");
  return `data:image/svg+xml;charset=utf-8,${encoded}`;
}

// ---------------- App ----------------

function LinkViewAPI() {
  const cyRef = useRef(null);

    const [controller] = useMaterialUIController();
    const darkModeContext = useMaterialUIController();
    const { darkMode } = controller;

  // Static metadata: id -> {id, type, parent, label, hasChildren}
  const staticNodesRef = useRef({});
  // Parent map: child -> parent
  const parentMapRef = useRef({});

  // View state
  const [viewMode, setViewMode] = useState("top-level"); // "top-level" or "children"
  const [currentStation, setCurrentStation] = useState(null);
  const [currentLocation, setCurrentLocation] = useState(null); // Track location string for API calls
  const [loading, setLoading] = useState(false);

  // UI controls - initialized to null, will be set from API preferences or defaults
  const [nodeSize, setNodeSize] = useState(null);
  const [fontSize, setFontSize] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(null);
  const [labelPosition, setLabelPosition] = useState(null);
  const [labelDistance, setLabelDistance] = useState(null);

  const [initialized, setInitialized] = useState(false);
  const [preferencesReady, setPreferencesReady] = useState(false); // Track when preferences are loaded
  const canSavePreferences = useRef(false); // Only allow saves after first preference load
  const savedPan = useRef(null); // Store pan position from API preferences
  const [debugDataUri, setDebugDataUri] = useState(null);
  const [debugDecodedSvg, setDebugDecodedSvg] = useState(null);
  const containerBgRef = useRef("transparent");
  const [severityColors, setSeverityColors] = useState(null);
  const [svgTemplates, setSvgTemplates] = useState(null);

  const [contextMenu, setContextMenu] = useState({
    visible: false,
    x: 0,
    y: 0,
    nodeId: null,
    nodeLabel: "",
    alarms: [],
    nodeData: null,
  });
  
  const [showClearLayoutConfirm, setShowClearLayoutConfirm] = useState(false);

  {
    /* Debug panel: shows one node's SVG preview and raw decoded SVG for inspection */
  }
  {
    debugDataUri && (
      <div
        style={{
          position: "fixed",
          right: 12,
          bottom: 12,
          width: 360,
          maxHeight: 320,
          background: "#fff",
          border: "1px solid #ccc",
          padding: 8,
          overflow: "auto",
          zIndex: 10000,
        }}
      >
        <div style={{ fontWeight: "bold", marginBottom: 6 }}>
          Debug: sample node SVG
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <div
            style={{
              width: 120,
              height: 120,
              border: "1px solid #eee",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <img
              alt="svg-preview"
              src={debugDataUri}
              style={{ maxWidth: "100%", maxHeight: "100%" }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 12, color: "#666", marginBottom: 6 }}>
              Data URI (truncated):
            </div>
            <textarea
              readOnly
              value={debugDataUri}
              style={{ width: "100%", height: 60, fontSize: 11 }}
            />
          </div>
        </div>
        <div style={{ marginTop: 8, fontSize: 12, color: "#666" }}>
          Decoded SVG:
        </div>
        <textarea
          readOnly
          value={debugDecodedSvg || ""}
          style={{ width: "100%", height: 120, fontSize: 11 }}
        />
      </div>
    );
  }
  // -------- Label positioning --------

  function getLabelStyles() {
    const pos = labelPosition ?? "above";
    const dist = labelDistance ?? 10;
    
    return {
      "text-margin-y":
        pos === "above"
          ? -dist
          : pos === "below"
          ? dist
          : 0,
      "text-margin-x":
        pos === "left" ? -dist : pos === "right" ? dist : 0,
      "text-valign":
        pos === "above"
          ? "top"
          : pos === "below"
          ? "bottom"
          : "center",
      "text-halign":
        pos === "left"
          ? "left"
          : pos === "right"
          ? "right"
          : "center",
    };
  }

  // -------- Stylesheet (with transparency fix) --------

  const stylesheet = [
    {
      selector: "node",
      style: {
        "background-color": "transparent",
        "background-image": "data(iconSvg)",
        "background-fit": "contain",
        "background-clip": "none",
        "background-image-opacity": 1,
        "background-opacity": 0,
        "background-blacken": 0,
        "border-width": 0,
        "border-opacity": 0,

        label: "data(label)",
        "text-background-opacity": 0,
        color:  darkMode ? "#eee":"#000",

        width: nodeSize ?? 85,
        height: nodeSize ?? 85,
        ...getLabelStyles(),
        "font-size": fontSize ?? 14,
      },
    },
    {
      selector: "node[hasChildren]",
      style: {
        cursor: "pointer",
      },
    },
    {
      selector: "edge",
      style: {
        width: 4,
        "line-color": (ele) =>
          ele.data("status") === "down" ? "#ff1744" : "#4caf50",
        "curve-style": "straight",
      },
    },
  ];

  // -------- Fetch and build station nodes --------

  async function loadTopLevelView(cy) {
    setLoading(true);
    try {
      
      // Fetch nodes
      const data = await fetchStationNodes();
      const staticNodes = {};
      const parentMap = {};

      data.nodes.forEach((n) => {
        staticNodes[n.id] = n;
        if (n.parent) parentMap[n.id] = n.parent;
      });

      staticNodesRef.current = staticNodes;
      parentMapRef.current = parentMap;

      buildGraph(cy, data.nodes, parentMap, "top-level", "top-level", null);
      setViewMode("top-level");
      setCurrentStation(null);
      setCurrentLocation(null);
      
      setInitialized(true); // Mark as initialized after successful load
      
      // Always set zoom to 1 and pan to (100, 100)
      setZoomLevel(1);
      cy.zoom(1);
      cy.pan({ x: 100, y: 100 });
    } catch (error) {
      console.error("Failed to load stations:", error);
    } finally {
      setLoading(false);
    }
  }

  // -------- Fetch and build child nodes --------

  async function loadChildrenView(cy, location) {
    setLoading(true);
    try {
      const data = await fetchChildNodes(location);
      const staticNodes = {};
      const parentMap = {};

      data.nodes.forEach((n) => {
        staticNodes[n.id] = n;
        if (n.parent) parentMap[n.id] = n.parent;
      });

      staticNodesRef.current = staticNodes;
      parentMapRef.current = parentMap;

      buildGraph(cy, data.nodes, parentMap, `children_${location}`, "children", location);
      setViewMode("children");
      setCurrentStation(data.parentStation);
      setCurrentLocation(location);
      
      // Always set zoom to 1 and pan to (100, 100)
      setZoomLevel(1);
      cy.zoom(1);
      cy.pan({ x: 100, y: 100 });
    } catch (error) {
      console.error("Failed to load children:", error);
    } finally {
      setLoading(false);
    }
  }

  // -------- Build graph from nodes --------

  function buildGraph(cy, nodes, parentMap, viewKey, viewModeParam = "top-level", locationParam = null) {
    if (!severityColors || svgTemplates === null) {
      return; // wait for colors and templates to load
    }
    const elements = [];
    const nodesToSavePositions = []; // Track nodes that need positions saved
    const nodeStatus = {}; // Track node statuses for edge coloring
    
    // Dynamic snake pattern layout config based on viewport and node size
    const currentNodeSize = nodeSize ?? 85;
    // Get actual container width instead of cy.width()
    const container = cy.container();
    const viewportWidth = container ? container.clientWidth : 1200; // fallback to 1200 if no container
    
    // Calculate spacing as a ratio of node size (2.5x node size for spacing between nodes)
    const horizontalSpacing = currentNodeSize * 2.5;
    const verticalSpacing = currentNodeSize * 2.5;
    
    // Calculate how many nodes fit per row based on viewport width
    const padding = 50; // Padding from viewport edges
    const usableWidth = viewportWidth - (2 * padding);
    
    // Actual layout formula: first node at startX, then each subsequent node at startX + col * horizontalSpacing
    // So n nodes span: (n-1) * horizontalSpacing pixels
    // We need: (n-1) * horizontalSpacing <= usableWidth
    // Therefore: n <= (usableWidth / horizontalSpacing) + 1
    const itemsPerRow = Math.max(3, Math.floor(usableWidth / horizontalSpacing) + 1); // Minimum 3 nodes per row
    
    const startX = 0; // Starting X position
    const startY = 0; // Starting Y position
    let nodeIndex = 0; // Track node position for snake pattern
    
    // detect container background color
    try {
      const cont = cy.container && cy.container();
      if (cont && typeof window !== "undefined") {
        const comp = window.getComputedStyle(cont);
        const rawBg =
          comp && comp.backgroundColor ? comp.backgroundColor : "#fafafa";
        containerBgRef.current = rgbToHex(rawBg);
      } else {
        containerBgRef.current = "#fafafa";
      }
    } catch (e) {
      containerBgRef.current = "#fafafa";
    }

    nodes.forEach((n) => {
      // Use severity directly from API (already calculated by backend)
      const sev = n.severity !== undefined ? n.severity : 0;
      const childSev = n.childrenSeverity || 0;
      const color = severityColor(sev, severityColors);
      const iconSvg = buildSvgDataUri(n.type, color, containerBgRef.current, svgTemplates, childSev, severityColors);
      const count = n.alarms.length;
      const label = count ? `${n.label} (${count})` : n.label;

      // Debug first node
      if (elements.length === 0) {
        setDebugDataUri(iconSvg);
        try {
          const parts = iconSvg.split(",");
          if (parts[0].includes("base64")) {
            const b64 = parts[1];
            const decoded =
              typeof window !== "undefined" && window.atob
                ? decodeURIComponent(escape(window.atob(b64)))
                : Buffer.from(b64, "base64").toString("utf8");
            setDebugDecodedSvg(decoded);
          } else {
            const decoded = decodeURIComponent(parts[1]);
            setDebugDecodedSvg(decoded);
          }
        } catch (e) {
          // ignore decode errors
        }
      }
      
      const data = {
        id: n.id,
        label,
        type: n.type,
        status: n.status,
        severity: sev,
        alarms: n.alarms,
        iconSvg,
        hasChildren: n.hasChildren || false,
        location: n.location || null,
        childrenSeverity: n.childrenSeverity || 0,
        ipAddress: n.ipAddress || n.ip || n.IP || n.ipAddr || null,
      };

      // Track node status for edge coloring
      nodeStatus[n.id] = n.status;

      const el = { data };
      
      // Priority: 1) API position, 2) Snake pattern position
      if (n.position_x !== null && n.position_y !== null) {
        el.position = { x: n.position_x, y: n.position_y };
      } else {
        // Generate snake pattern position if no API position
        const row = Math.floor(nodeIndex / itemsPerRow);
        const col = nodeIndex % itemsPerRow;
        
        // Snake pattern: even rows go left-to-right, odd rows go right-to-left
        const isEvenRow = row % 2 === 0;
        const x = isEvenRow 
          ? startX + (col * horizontalSpacing) 
          : startX + ((itemsPerRow - 1 - col) * horizontalSpacing);
        const y = startY + (row * verticalSpacing);
        
        el.position = { x, y };
        nodeIndex++;
        
        // Mark this node for position saving
        nodesToSavePositions.push({
          id: n.id,
          position: { x, y }
        });
      }
      
      elements.push(el);
    });

    // Create edges with correct status based on node status
    Object.entries(parentMap).forEach(([child, parent]) => {
      // Check if either parent or child node is down (status === "down" or status === false)
      const parentDown = nodeStatus[parent] === "down" || nodeStatus[parent] === false;
      const childDown = nodeStatus[child] === "down" || nodeStatus[child] === false;
      const down = parentDown || childDown;
            
      elements.push({
        data: {
          id: `e${parent}-${child}`,
          source: parent,
          target: child,
          status: down ? "down" : "up",
        },
      });
    });

    cy.elements().remove();
    const added = cy.add(elements);

    // Enable user interaction
    cy.userPanningEnabled(true);
    cy.userZoomingEnabled(true);
    cy.boxSelectionEnabled(false);

    // Don't set zoom or position here - let calling function handle viewport
    
    // Save generated positions to API
    if (nodesToSavePositions.length > 0) {
      nodesToSavePositions.forEach(({ id, position }) => {
        saveNodePosition(
          id,
          position,
          viewModeParam,
          locationParam
        ).catch((error) => {
          console.error(`Failed to save position for node ${id}:`, error);
        });
      });
    }
  }

  // -------- Initial graph --------

  async function initGraph(cy) {
    await loadTopLevelView(cy);

    // Track if node was dragged to prevent click action
    let draggedNode = false;

    cy.on("grab", "node", () => {
      draggedNode = false;
    });

    cy.on("drag", "node", () => {
      draggedNode = true;
    });

    cy.on("tap", "node", (evt) => {      
      if (draggedNode) {
        draggedNode = false;
        return;
      }

      const node = evt.target;
      const hasChildren = node.data("hasChildren");
      const location = node.data("location");
      
      if (hasChildren && viewMode === "top-level") {
        loadChildrenView(cy, location);
      } else {
      }
    });

    cy.on("cxttap", "node", (evt) => {
      evt.originalEvent.preventDefault(); // Prevent default browser context menu
      const node = evt.target;
      const p = evt.renderedPosition;
      const alarms = node.data("alarms") || [];
      const nodeType = node.data("type");
      const ipAddress = node.data("ipAddress");
            
      // Get managementURL from svgTemplates for this node type
      const categoryData = svgTemplates[nodeType];
      const managementURL = categoryData && typeof categoryData === 'object' ? categoryData.managementURL : null;
            
      setContextMenu({
        visible: true,
        x: p.x,
        y: p.y,
        nodeId: node.id(),
        nodeLabel: node.data("label"),
        alarms,
        nodeData: {
          id: node.id(),
          type: nodeType,
          status: node.data("status"),
          severity: node.data("severity"),
          childrenSeverity: node.data("childrenSeverity"),
          hasChildren: node.data("hasChildren"),
          location: node.data("location"),
          ipAddress: ipAddress,
          managementURL: managementURL,
        },
      });
    });

    cy.on("tap", (evt) => {
      if (evt.target === cy) {
        setContextMenu((m) => ({ ...m, visible: false }));
      }
    });
  }

  // -------- Save positions on drag --------

  useEffect(() => {
    const cy = cyRef.current;
    if (!cy || !initialized) return;

    const handleDragFree = async (evt) => {
      const node = evt.target;
      const position = node.position();
      const nodeId = node.id();

      // Adjust position to account for pan offset
      // Current pan position
      const currentPan = cy.pan();
      // Default pan is (100, 100)
      const defaultPan = { x: 100, y: 100 };
      
      // Calculate pan offset from default
      const panOffset = {
        x: currentPan.x - defaultPan.x,
        y: currentPan.y - defaultPan.y
      };
      
      // Add the pan offset to the position so it appears in the same place when loaded
      const adjustedPosition = {
        x: position.x + panOffset.x,
        y: position.y + panOffset.y
      };

      // Call API to save adjusted position
      try {
        await saveNodePosition(
          nodeId,
          adjustedPosition,
          viewMode,
          viewMode === "children" ? currentStation?.id : null
        );
      } catch (error) {
        console.error("Failed to save node position:", error);
      }
    };

    cy.on("dragfree", "node", handleDragFree);

    return () => {
      cy.off("dragfree", "node", handleDragFree);
    };
  }, [viewMode, currentStation, initialized]);

  // -------- Save pan position --------

  useEffect(() => {
    const cy = cyRef.current;
    if (!cy || !initialized) return;

    const handlePan = () => {
      // Pan is disabled - don't save it
      const preferences = {
        nodeSize,
        fontSize,
        labelPosition,
        labelDistance,
      };

      saveViewPreferences(preferences, "global", null).catch((error) => {
        console.error("Failed to save pan position:", error);
      });
    };

    cy.on("pan", handlePan);

    return () => {
      cy.off("pan", handlePan);
    };
  }, [
    initialized,
    nodeSize,
    fontSize,
    zoomLevel,
    labelPosition,
    labelDistance,
  ]);

  // -------- Update status only --------

  async function refreshStatus(cy) {
    const staticNodes = staticNodesRef.current;
    const parentMap = parentMapRef.current;

    const nodeIds = Object.keys(staticNodes);
    if (nodeIds.length === 0) return;

    try {
      const updates = await fetchStatusUpdate(nodeIds, viewMode, currentLocation);
      const nodeStatus = {};

      Object.entries(updates).forEach(([id, info]) => {
        const node = cy.$id(id);
        if (!node || node.length === 0) return;
        const meta = staticNodes[id];
        if (!meta) return;

        // Use severity directly from API (already calculated by backend)
        const sev = info.severity !== undefined ? info.severity : 0;
        const childSev = info.childrenSeverity || 0;
        const color = severityColor(sev, severityColors);
        const iconSvg = buildSvgDataUri(
          meta.type,
          color,
          containerBgRef.current,
          svgTemplates,
          childSev,
          severityColors
        );
        const count = info.alarms.length;
        const label = count ? `${meta.label} (${count})` : meta.label;

        node.data("status", info.status);
        node.data("severity", sev);
        node.data("alarms", info.alarms);
        node.data("label", label);
        node.data("iconSvg", iconSvg);

        nodeStatus[id] = info.status;
      });

      Object.entries(parentMap).forEach(([child, parent]) => {
        const edge = cy.$id(`e${parent}-${child}`);
        if (!edge || edge.length === 0) return;
        // Check if either parent or child node is down (status === "down" or status === false)
        const parentDown = nodeStatus[parent] === "down" || nodeStatus[parent] === false;
        const childDown = nodeStatus[child] === "down" || nodeStatus[child] === false;
        const down = parentDown || childDown;
        edge.data("status", down ? "down" : "up");
      });

      cy.style().update();
    } catch (error) {
      console.error("Failed to refresh status:", error);
    }
  }

  // -------- Load SVG templates on mount --------

  useEffect(() => {
    fetchSvgTemplates()
      .then((templates) => {
        setSvgTemplates(templates);
        svgTemplatesCache = templates; // Update cache
      })
      .catch((error) => {
        console.error("Failed to load SVG templates:", error);
        // Use empty object - will fall back to hardcoded templates
        setSvgTemplates({});
      });
  }, []);

  // -------- Load severity colors on mount --------

  useEffect(() => {
    fetchSeverityColors()
      .then((colors) => {
        setSeverityColors(colors);
      })
      .catch((error) => {
        console.error("Failed to load severity colors:", error);
        // Fallback to default colors (matching mockApi.js fallback)
        setSeverityColors([
          { id: 0, severity: "NONE", color: "#3f51b5" },
          { id: 1, severity: "INFO", color: "#9e9e9e" },
          { id: 2, severity: "WARNING", color: "#00a2ff" },
          { id: 3, severity: "MINOR", color: "#ffeb3b" },
          { id: 4, severity: "MAJOR", color: "#ff9100" },
          { id: 5, severity: "CRITICAL", color: "#ff1744" },
          { id: 6, severity: "SEVERITY_6", color: "#f50057" },
          { id: 7, severity: "SEVERITY_7", color: "#d32f2f" },
          { id: 8, severity: "SEVERITY_8", color: "#d81b60" },
          { id: 9, severity: "SEVERITY_9", color: "#c2185b" },
          { id: 10, severity: "SEVERITY_10", color: "#ad1457" },
          { id: 11, severity: "SEVERITY_11", color: "#880e4f" },
          { id: 12, severity: "SEVERITY_12", color: "#d32f2f" },
          { id: 13, severity: "SEVERITY_13", color: "#c62828" },
          { id: 14, severity: "SEVERITY_14", color: "#b71c1c" },
          { id: 15, severity: "SEVERITY_15", color: "#a50000" },
          { id: 16, severity: "SEVERITY_16", color: "#940000" },
          { id: 17, severity: "SEVERITY_17", color: "#830000" },
          { id: 18, severity: "SEVERITY_18", color: "#720000" },
          { id: 19, severity: "SEVERITY_19", color: "#610000" },
          { id: 20, severity: "SEVERITY_20", color: "#500000" },
          { id: 21, severity: "SEVERITY_21", color: "#3f0000" }
        ]);
      });
  }, []);

  // -------- Load saved preferences on mount --------

  useEffect(() => {
    fetchViewPreferences("global", null)
      .then((response) => {
        const prefs = response.preferences;
        
        // Apply preferences from API or use defaults
        const appliedNodeSize = prefs?.nodeSize ?? 85;
        const appliedFontSize = prefs?.fontSize ?? 14;
        const appliedLabelPosition = prefs?.labelPosition ?? "above";
        const appliedLabelDistance = prefs?.labelDistance ?? 10;
                
        setNodeSize(appliedNodeSize);
        setFontSize(appliedFontSize);
        setLabelPosition(appliedLabelPosition);
        setLabelDistance(appliedLabelDistance);
        
        // Always use fixed zoom level
        setZoomLevel(1);
                
        // Enable saves immediately after applying preferences
        canSavePreferences.current = true;
        
        // Mark preferences as ready - initGraph will be called by useEffect
        setPreferencesReady(true);
      })
      .catch((error) => {        
        // Use default values on error
        setNodeSize(85);
        setFontSize(14);
        setZoomLevel(1);
        setLabelPosition("above");
        setLabelDistance(10);
        
        // Enable saves and mark as ready even on error
        canSavePreferences.current = true;
        
        // Mark preferences as ready with defaults
        setPreferencesReady(true);
      });
  }, []);

  // -------- Initialize graph when preferences and colors are ready --------

  useEffect(() => {
    const cy = cyRef.current;
    if (!cy || !preferencesReady || !severityColors || svgTemplates === null || initialized) {
      return;
    }
    
    initGraph(cy).catch(err => {
      console.error('❌ Error initializing graph:', err);
    });
  }, [preferencesReady, severityColors, svgTemplates, initialized]);

  // -------- Status refresh interval --------

  useEffect(() => {
    const cy = cyRef.current;
    if (!cy || !initialized) {
      return; // Wait until graph is initialized
    }
    
    const interval = setInterval(() => {
      refreshStatus(cy);
    }, 5000);
    
    return () => {
      clearInterval(interval);
    };
  }, [initialized, viewMode, currentLocation]);

  // -------- Preference persistence --------

  useEffect(() => {
    if (cyRef.current) cyRef.current.style().update();
  }, [nodeSize]);

  useEffect(() => {
    if (cyRef.current) cyRef.current.style().update();
  }, [fontSize]);

  useEffect(() => {
    if (cyRef.current) cyRef.current.zoom(zoomLevel);
  }, [zoomLevel]);

  useEffect(() => {
    if (cyRef.current) cyRef.current.style().update();
  }, [labelPosition]);

  useEffect(() => {
    if (cyRef.current) cyRef.current.style().update();
  }, [labelDistance]);

  // -------- Save preferences to API --------

  useEffect(() => {    
    // Don't save until graph is initialized
    if (!initialized) {
      return;
    }
    
    // Don't save if any preference value is still null (not loaded yet)
    if (nodeSize === null || fontSize === null || zoomLevel === null || 
        labelPosition === null || labelDistance === null) {
      return;
    }
    
    // Don't save until preferences have been loaded from API
    if (!canSavePreferences.current) {
      return;
    }

    const cy = cyRef.current;
    const pan = cy ? cy.pan() : { x: 0, y: 0 };
    const preferences = {
      nodeSize,
      fontSize,
      zoomLevel,
      labelPosition,
      labelDistance,
      pan,
    };

    saveViewPreferences(preferences, "global", null).catch((error) => {
      console.error("Failed to save view preferences:", error);
    });
  }, [
    nodeSize,
    fontSize,
    zoomLevel,
    labelPosition,
    labelDistance,
    initialized,
  ]);

  // -------- Render helpers --------

  function groupedAlarms(alarms, severityColors) {
    const groups = {};
    SEVERITY_ORDER.forEach((sevId) => (groups[sevId] = []));
    (alarms || []).forEach((a) => {
      const sevId = a.severity || 5; // Default to INFO
      if (!groups[sevId]) groups[sevId] = [];
      groups[sevId].push(a);
    });
    return groups;
  }

  // -------- Render --------
  
  return (
    <div 
      style={{ height: "100vh", width: "100%", position: "relative" }}
      onContextMenu={(e) => e.preventDefault()}
    >
      <div
        style={{
          padding: "10px",
          background: darkModeContext[0]?.darkMode
            ? "modifiedDarkTheme"
            : "default",
          display: "flex",
          alignItems: "center",
          gap: "20px",
        }}
      >
        {viewMode === "children" && currentStation && (
          <button
            onClick={() => loadTopLevelView(cyRef.current)}
            style={{
              padding: "10px 12px",
              background: darkMode ? "#2196f3" : "#1976d2",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: "bold",
              fontSize: "18px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "44px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = darkMode ? "#1976d2" : "#1565c0";
              e.currentTarget.style.transform = "scale(1.05)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = darkMode ? "#2196f3" : "#1976d2";
              e.currentTarget.style.transform = "scale(1)";
            }}
            title="Back to Top-Level View"
          >
            ←
          </button>
        )}
        
        <button
          onClick={() => {
            const cy = cyRef.current;
            if (cy) {
              // Reset zoom and pan
              cy.zoom(1);
              cy.pan({ x: 100, y: 100 });
              
              // Reload the current view from API
              if (viewMode === "top-level") {
                loadTopLevelView(cy);
              } else if (viewMode === "children" && currentLocation) {
                loadChildrenView(cy, currentLocation);
              }
            }
          }}
          style={{
            padding: "10px 12px",
            background: darkMode ? "#4caf50" : "#388e3c",
            color: "#fff",
            border: "none",
            borderRadius: "6px",
            cursor: "pointer",
            fontWeight: "bold",
            fontSize: "18px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "44px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
            transition: "all 0.2s",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = darkMode ? "#388e3c" : "#2e7d32";
            e.currentTarget.style.transform = "scale(1.05)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = darkMode ? "#4caf50" : "#388e3c";
            e.currentTarget.style.transform = "scale(1)";
          }}
          title="Reload View"
        >
          ↻
        </button>

        {viewMode === "children" && currentStation && (
          <div style={{ fontWeight: "bold", fontSize: "18px", color: darkMode ? "#fff" : "#333" }}>
            {currentStation.label.replace(/^Station\s+/i, '')}
          </div>
        )}

        {loading && (
          <div style={{ color: "#666", fontStyle: "italic" }}>Loading...</div>
        )}

        <div
          style={{
            marginLeft: "auto",
            display: "flex",
            gap: "15px",
            alignItems: "flex-end",
          }}
        >
          <label style={{ color: darkMode ? "#fff" : "#333", display: "flex", flexDirection: "column", gap: "4px" }}>
            <span style={{ fontSize: "11px", fontWeight: "600" }}>Node Size: {nodeSize ?? 85}</span>
            <input
              type="range"
              min="40"
              max="150"
              value={nodeSize ?? 85}
              onChange={(e) => setNodeSize(Number(e.target.value))}
              style={{ width: "140px" }}
            />
          </label>

          <label style={{ color: darkMode ? "#fff" : "#333", display: "flex", flexDirection: "column", gap: "4px" }}>
            <span style={{ fontSize: "11px", fontWeight: "600" }}>Font Size: {fontSize ?? 14}</span>
            <input
              type="range"
              min="6"
              max="30"
              value={fontSize ?? 14}
              onChange={(e) => setFontSize(Number(e.target.value))}
              style={{ width: "140px" }}
            />
          </label>

          <label style={{ color: darkMode ? "#fff" : "#333", display: "flex", flexDirection: "column", gap: "4px" }}>
            <span style={{ fontSize: "11px", fontWeight: "600" }}>Zoom: {(zoomLevel ?? 1).toFixed(2)}</span>
            <input
              type="range"
              min="0.2"
              max="2"
              step="0.05"
              value={zoomLevel ?? 1}
              onChange={(e) => setZoomLevel(Number(e.target.value))}
              style={{ width: "140px" }}
            />
          </label>

          <label style={{ color: darkMode ? "#fff" : "#333", display: "flex", flexDirection: "column", gap: "4px" }}>
            <span style={{ fontSize: "11px", fontWeight: "600" }}>Label Position</span>
            <select
              value={labelPosition ?? "above"}
              onChange={(e) => setLabelPosition(e.target.value)}
              style={{ 
                minHeight: "30px",
                background: darkMode ? "#424242" : "#fff",
                color: darkMode ? "#fff" : "#333",
                border: darkMode ? "1px solid #666" : "1px solid #ccc",
                borderRadius: "4px",
                padding: "4px 8px",
                fontSize: "12px"
              }}
            >
              <option value="above">Above</option>
              <option value="below">Below</option>
              <option value="left">Left</option>
              <option value="right">Right</option>
            </select>
          </label>

          <label style={{ color: darkMode ? "#fff" : "#333", display: "flex", flexDirection: "column", gap: "4px" }}>
            <span style={{ fontSize: "11px", fontWeight: "600" }}>Label Distance: {labelDistance ?? 10}</span>
            <input
              type="range"
              min="3"
              max="120"
              value={labelDistance ?? 10}
              onChange={(e) => setLabelDistance(Number(e.target.value))}
              style={{ width: "140px" }}
            />
          </label>
          
          <button
            onClick={() => setShowClearLayoutConfirm(true)}
            style={{
              padding: "10px 16px",
              background: darkMode ? "#d32f2f" : "#c62828",
              color: "#fff",
              border: "none",
              borderRadius: "6px",
              cursor: "pointer",
              fontWeight: "bold",
              fontSize: "13px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "6px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
              transition: "all 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = darkMode ? "#c62828" : "#b71c1c";
              e.currentTarget.style.transform = "scale(1.05)";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = darkMode ? "#d32f2f" : "#c62828";
              e.currentTarget.style.transform = "scale(1)";
            }}
            title="Clear Layout (Reset to Default)"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M3 6h18M8 6V4c0-1.1.9-2 2-2h4c1.1 0 2 .9 2 2v2m3 0v14c0 1.1-.9 2-2 2H7c-1.1 0-2-.9-2-2V6h14zM10 11v6M14 11v6" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span>Clear Layout</span>
          </button>
        </div>
      </div>

      <CytoscapeComponent
        elements={[]}
        stylesheet={stylesheet}
        cy={(cy) => (cyRef.current = cy)}
        style={{
          width: "100%",
          height: "90vh",
          background: darkModeContext[0]?.darkMode
            ? "modifiedDarkTheme"
            : "default",
        }} // theme background
        panningEnabled={true}
        userZoomingEnabled={true}
        autoungrabify={false}
      />

      {contextMenu.visible && (
        <div
          style={{
            position: "fixed",
            top: contextMenu.y,
            left: contextMenu.x,
            background: darkModeContext[0]?.darkMode
              ? "#1a1a1a"
              : "#ffffff",
            border: darkModeContext[0]?.darkMode
              ? "1px solid #444"
              : "1px solid #ddd",
            padding: "8px 0",
            boxShadow: darkModeContext[0]?.darkMode
              ? "0 4px 16px rgba(0,0,0,0.6)"
              : "0 4px 16px rgba(0,0,0,0.15)",
            borderRadius: "8px",
            zIndex: 9999,
            minWidth: "260px",
            maxWidth: "400px",
          }}
        >
          <div
            style={{
              padding: "8px 16px",
              fontWeight: "bold",
              borderBottom: darkModeContext[0]?.darkMode
                ? "1px solid #333"
                : "1px solid #e0e0e0",
              color: darkModeContext[0]?.darkMode ? "#fff" : "#333",
              fontSize: "14px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <span>{contextMenu.nodeLabel}</span>
            <button
              onClick={() => setContextMenu((m) => ({ ...m, visible: false }))}
              style={{
                background: "transparent",
                border: "none",
                color: darkModeContext[0]?.darkMode ? "#aaa" : "#666",
                cursor: "pointer",
                fontSize: "20px",
                padding: "0",
                width: "24px",
                height: "24px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: "4px",
                transition: "background 0.2s",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = darkModeContext[0]?.darkMode ? "#333" : "#f0f0f0";
                e.currentTarget.style.color = darkModeContext[0]?.darkMode ? "#fff" : "#000";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.color = darkModeContext[0]?.darkMode ? "#aaa" : "#666";
              }}
            >
              ×
            </button>
          </div>

          {/* Node Information Section */}
          <div
            style={{
              padding: "6px 16px",
              fontSize: "12px",
              color: darkModeContext[0]?.darkMode ? "#aaa" : "#666",
              borderBottom: darkModeContext[0]?.darkMode
                ? "1px solid #333"
                : "1px solid #e0e0e0",
              textTransform: "uppercase",
              fontWeight: "500",
            }}
          >
            Node Information
          </div>
          {contextMenu.nodeData && (
            <div style={{ padding: "8px 16px" }}>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "100px 1fr",
                  gap: "6px",
                  fontSize: "13px",
                }}
              >
                <span
                  style={{
                    fontWeight: "600",
                    color: darkModeContext[0]?.darkMode ? "#aaa" : "#666",
                  }}
                >
                  Type:
                </span>
                <span
                  style={{
                    color: darkModeContext[0]?.darkMode ? "#fff" : "#333",
                    textTransform: "capitalize",
                  }}
                >
                  {contextMenu.nodeData.type}
                </span>

                <span
                  style={{
                    fontWeight: "600",
                    color: darkModeContext[0]?.darkMode ? "#aaa" : "#666",
                  }}
                >
                  Status:
                </span>
                <span
                  style={{
                    color:
                      contextMenu.nodeData.status === "up" || contextMenu.nodeData.status === true
                        ? "#4caf50"
                        : "#f44336",
                    fontWeight: "600",
                    textTransform: "uppercase",
                  }}
                >
                  {contextMenu.nodeData.status === true || contextMenu.nodeData.status === "up" ? "UP" : "DOWN"}
                </span>

                <span
                  style={{
                    fontWeight: "600",
                    color: darkModeContext[0]?.darkMode ? "#aaa" : "#666",
                  }}
                >
                  Severity:
                </span>
                <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                  <span
                    style={{
                      width: 12,
                      height: 12,
                      borderRadius: "50%",
                      backgroundColor: severityColor(contextMenu.nodeData.severity, severityColors),
                      display: "inline-block",
                    }}
                  />
                  <span
                    style={{
                      color: darkModeContext[0]?.darkMode ? "#fff" : "#333",
                    }}
                  >
                    {(() => {
                      const sev = severityColors?.find(s => s.id === contextMenu.nodeData.severity);
                      return sev ? `${sev.severity} (${contextMenu.nodeData.severity})` : contextMenu.nodeData.severity;
                    })()}
                  </span>
                </div>

                {contextMenu.nodeData.childrenSeverity > 0 && (
                  <>
                    <span
                      style={{
                        fontWeight: "600",
                        color: darkModeContext[0]?.darkMode ? "#aaa" : "#666",
                      }}
                    >
                      Children Severity:
                    </span>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                      <span
                        style={{
                          width: 12,
                          height: 12,
                          borderRadius: "50%",
                          backgroundColor: severityColor(contextMenu.nodeData.childrenSeverity, severityColors),
                          display: "inline-block",
                        }}
                      />
                      <span
                        style={{
                          color: darkModeContext[0]?.darkMode ? "#fff" : "#333",
                        }}
                      >
                        {(() => {
                          const sev = severityColors?.find(s => s.id === contextMenu.nodeData.childrenSeverity);
                          return sev ? `${sev.severity} (${contextMenu.nodeData.childrenSeverity})` : contextMenu.nodeData.childrenSeverity;
                        })()}
                      </span>
                    </div>
                  </>
                )}

                {contextMenu.nodeData.location && (
                  <>
                    <span
                      style={{
                        fontWeight: "600",
                        color: darkModeContext[0]?.darkMode ? "#aaa" : "#666",
                      }}
                    >
                      Location:
                    </span>
                    <span
                      style={{
                        color: darkModeContext[0]?.darkMode ? "#fff" : "#333",
                      }}
                    >
                      {contextMenu.nodeData.location}
                    </span>
                  </>
                )}

                {contextMenu.nodeData.ipAddress && (
                  <>
                    <span
                      style={{
                        fontWeight: "600",
                        color: darkModeContext[0]?.darkMode ? "#aaa" : "#666",
                      }}
                    >
                      IP Address:
                    </span>
                    <span
                      style={{
                        color: darkModeContext[0]?.darkMode ? "#fff" : "#333",
                        fontFamily: "monospace",
                      }}
                    >
                      {contextMenu.nodeData.ipAddress}
                    </span>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Management Interface Button */}
          {contextMenu.nodeData && (contextMenu.nodeData.managementURL || contextMenu.nodeData.ipAddress) && (
            <div style={{ padding: "8px 16px", borderBottom: darkModeContext[0]?.darkMode ? "1px solid #333" : "1px solid #e0e0e0" }}>
              <button
                onClick={() => {
                  const ipAddr = contextMenu.nodeData.ipAddress;
                  let url = contextMenu.nodeData.managementURL;
                  
                  if (url && ipAddr) {
                    // Replace IPADDR placeholder with actual IP
                    url = url.replace(/IPADDR/g, ipAddr).replace(/{{ipAddress}}/g, ipAddr).replace(/{{ipAddres}}/g, ipAddr);
                  } else if (ipAddr && !url) {
                    // If no managementURL, construct default URL with IP
                    url = `http://${ipAddr}`;
                  }
                  
                  if (url) {
                    window.open(url, "_blank");
                  }
                  setContextMenu((m) => ({ ...m, visible: false }));
                }}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  backgroundColor: darkModeContext[0]?.darkMode ? "#2196f3" : "#1976d2",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  fontSize: "13px",
                  fontWeight: "600",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "6px",
                  transition: "background-color 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = darkModeContext[0]?.darkMode ? "#1976d2" : "#1565c0";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = darkModeContext[0]?.darkMode ? "#2196f3" : "#1976d2";
                }}
              >
                <span>🔗</span>
                <span>Open Management Interface</span>
              </button>
            </div>
          )}

          <div
            style={{
              padding: "6px 16px",
              fontSize: "12px",
              color: darkModeContext[0]?.darkMode ? "#aaa" : "#666",
              borderBottom: darkModeContext[0]?.darkMode
                ? "1px solid #333"
                : "1px solid #e0e0e0",
              textTransform: "uppercase",
              fontWeight: "500",
            }}
          >
            Alarms
          </div>

          {(() => {
            const groups = groupedAlarms(contextMenu.alarms, severityColors);
            return SEVERITY_ORDER.map((sevId) => {
              const items = groups[sevId];
              if (!items || !items.length) return null;
              const color = severityColor(sevId, severityColors);
              const severityObj = severityColors.find(s => s.id === sevId);
              const severityName = severityObj ? severityObj.severity : "UNKNOWN";
              return (
                <div key={sevId}>
                  <div
                    style={{
                      padding: "6px 16px",
                      fontWeight: "600",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                      color: darkModeContext[0]?.darkMode ? "#fff" : "#333",
                    }}
                  >
                    <span
                      style={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        backgroundColor: color,
                        display: "inline-block",
                      }}
                    />
                    <span>
                      {severityName.charAt(0) + severityName.slice(1).toLowerCase()} (
                      {items.length})
                    </span>
                  </div>
                  {items.map((a, idx) => (
                    <div
                      key={idx}
                      style={{
                        padding: "3px 16px 3px 36px",
                        fontSize: "13px",
                        color: darkModeContext[0]?.darkMode ? "#ccc" : "#555",
                        lineHeight: "1.5",
                      }}
                    >
                      • {a.text}
                    </div>
                  ))}
                </div>
              );
            });
          })()}
        </div>
      )}

      {/* Clear Layout Confirmation Dialog */}
      {showClearLayoutConfirm && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0, 0, 0, 0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 10000,
          }}
          onClick={() => setShowClearLayoutConfirm(false)}
        >
          <div
            style={{
              background: darkModeContext[0]?.darkMode ? "#1a1a1a" : "#ffffff",
              border: darkModeContext[0]?.darkMode ? "1px solid #444" : "1px solid #ddd",
              borderRadius: "12px",
              padding: "24px",
              maxWidth: "500px",
              boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
              <span style={{ fontSize: "32px" }}>⚠️</span>
              <h3 style={{ margin: 0, color: darkModeContext[0]?.darkMode ? "#fff" : "#333", fontSize: "20px" }}>
                Clear Layout Confirmation
              </h3>
            </div>
            <p style={{ color: darkModeContext[0]?.darkMode ? "#ccc" : "#666", fontSize: "14px", lineHeight: "1.6", margin: "16px 0" }}>
              This will reset all node positions to the default layout. All custom positioning will be permanently lost.
            </p>
            <p style={{ color: darkModeContext[0]?.darkMode ? "#ff9800" : "#f57c00", fontSize: "13px", fontWeight: "600", margin: "12px 0" }}>
              This action cannot be undone!
            </p>
            <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end", marginTop: "24px" }}>
              <button
                onClick={() => setShowClearLayoutConfirm(false)}
                style={{
                  padding: "10px 20px",
                  background: darkModeContext[0]?.darkMode ? "#424242" : "#e0e0e0",
                  color: darkModeContext[0]?.darkMode ? "#fff" : "#333",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontWeight: "600",
                  fontSize: "14px",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = darkModeContext[0]?.darkMode ? "#505050" : "#d0d0d0";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = darkModeContext[0]?.darkMode ? "#424242" : "#e0e0e0";
                }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  setShowClearLayoutConfirm(false);
                  const cy = cyRef.current;
                  if (cy) {
                    const result = await clearNodePositions(
                      viewMode,
                      viewMode === "children" ? currentLocation : null
                    );
                    
                    if (result.success) {
                      if (viewMode === "top-level") {
                        loadTopLevelView(cy);
                      } else if (viewMode === "children" && currentLocation) {
                        loadChildrenView(cy, currentLocation);
                      }
                    } else {
                      console.error('Failed to clear positions:', result.error);
                    }
                  }
                }}
                style={{
                  padding: "10px 20px",
                  background: darkModeContext[0]?.darkMode ? "#d32f2f" : "#c62828",
                  color: "#fff",
                  border: "none",
                  borderRadius: "6px",
                  cursor: "pointer",
                  fontWeight: "600",
                  fontSize: "14px",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = darkModeContext[0]?.darkMode ? "#c62828" : "#b71c1c";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = darkModeContext[0]?.darkMode ? "#d32f2f" : "#c62828";
                }}
              >
                Clear Layout
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default LinkViewAPI;
